import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FileUp, Link2, Loader2, Mic, Radio, Square, Trash2, Video, X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { reserveUpload, confirmUpload, putToPresignedUrl } from '@/features/geography/api'
import { sha256Hex } from '@/shared/lib/hash'
import { addEvidence, listEvidence, withdrawEvidence, type Evidence } from './api'
import { label } from './vocab'
import { LiveFeedPanel } from './LiveFeedPanel'
import { EvidenceViewer } from './EvidenceViewer'

/**
 * Evidence: upload, record, go live, link — and withdraw.
 *
 * ## Four ways in, one list out
 *
 * Every path ends at `POST /incidents/{id}/evidence`. Files go through the storage
 * feature's reserve → PUT → confirm dance; a browser recording produces a `Blob` and takes
 * the identical path, because a recording IS a file and giving it a second upload route
 * would give it a second set of bugs. Live reuses `coverage` — no `RTCPeerConnection` is
 * opened here.
 *
 * ## Withdrawal is not deletion
 *
 * Evidence is append-only. Withdrawing keeps the row, records who withdrew it and why, and
 * removes it from the live list. Nothing a human asserted is destroyed — that is what
 * makes this record able to survive being asked about in court.
 *
 * ## One failed upload must not discard the others
 *
 * Files are tracked individually with their own progress and their own failure. A batch
 * that fails as a batch means a reporter on a bad connection loses four good photos
 * because the fifth timed out.
 */

/** Client-side ceilings, a COURTESY only — the server refuses authoritatively. */
const MAX_AUDIO_SECONDS = 60
const MAX_VIDEO_SECONDS = 120

type Upload = {
  key: string
  name: string
  state: 'uploading' | 'done' | 'failed'
  error?: string
}

export function EvidencePanel({
  incidentId, canAttach = true, onChanged,
}: {
  incidentId: string
  /** False on a closed incident — the control is still offered; the server refuses. */
  canAttach?: boolean
  onChanged?: () => void
}) {
  const [rows, setRows] = useState<Evidence[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploads, setUploads] = useState<Upload[]>([])
  const [linkOpen, setLinkOpen] = useState(false)
  const [liveOpen, setLiveOpen] = useState(false)
  const [withdrawing, setWithdrawing] = useState<Evidence | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const reload = useCallback(async () => {
    try {
      setRows(await listEvidence(incidentId))
      setError(null)
    } catch (err) {
      setError(explain(err))
    }
  }, [incidentId])

  // `incidentId` is in the dependency list, via `reload`. With `[]` this would bind to
  // whatever id was current on first render and never refetch when the drawer opens on
  // another row.
  useEffect(() => { void reload() }, [reload])

  const attach = useCallback(async (
    blob: Blob, filename: string, kindCode: string, caption?: string | null,
  ) => {
    const key = `${filename}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setUploads((u) => [...u, { key, name: filename, state: 'uploading' }])
    try {
      const contentType = blob.type || 'application/octet-stream'
      const reserved = await reserveUpload({
        filename, contentType, sizeBytes: blob.size,
        purpose: 'INCIDENT_EVIDENCE',
        ownerEntityType: 'incident', ownerEntityId: incidentId,
      })
      // Straight to the presigned URL — NOT through `api`, which would attach a bearer
      // token and a request id the object store did not sign for and would reject.
      const file = new File([blob], filename, { type: contentType })
      await putToPresignedUrl(reserved.uploadUrl, file)
      // The checksum is REQUIRED by `/files/{id}/confirm`. Hash the same File that was
      // PUT, so it describes the bytes that actually went up.
      await confirmUpload(reserved.fileId, await sha256Hex(file))
      await addEvidence(incidentId, { kindCode, fileId: reserved.fileId, caption: caption ?? null })
      setUploads((u) => u.map((x) => (x.key === key ? { ...x, state: 'done' } : x)))
      await reload()
      onChanged?.()
    } catch (err) {
      setUploads((u) => u.map((x) => (
        x.key === key ? { ...x, state: 'failed', error: explain(err) } : x)))
    }
  }, [incidentId, reload, onChanged])

  const onFiles = (files: FileList | null) => {
    if (!files) return
    // Fired in parallel and tracked separately: one failure is one row's failure.
    for (const file of Array.from(files)) {
      void attach(file, file.name, kindOf(file.type))
    }
  }

  const live = rows?.filter((r) => !r.withdrawn) ?? []
  const withdrawn = rows?.filter((r) => r.withdrawn) ?? []

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} className="rounded-md border" />

      {/* Every control is OFFERED. A caller who lacks `incident:evidence` gets a refusal
          with a sentence naming the authority, which teaches; a missing button does not. */}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => fileInput.current?.click()}
          className="erp-btn h-9 px-3 inline-flex items-center gap-2 text-erp-sm">
          <FileUp className="h-3.5 w-3.5" /> Upload files
        </button>
        <input ref={fileInput} type="file" multiple hidden
          onChange={(e) => { onFiles(e.target.files); e.target.value = '' }} />

        <Recorder kind="AUDIO" onRecorded={attach} />
        <Recorder kind="VIDEO" onRecorded={attach} />

        <button type="button" onClick={() => setLiveOpen(true)}
          className="erp-btn h-9 px-3 inline-flex items-center gap-2 text-erp-sm">
          <Radio className="h-3.5 w-3.5" /> Go live
        </button>
        <button type="button" onClick={() => setLinkOpen(true)}
          className="erp-btn h-9 px-3 inline-flex items-center gap-2 text-erp-sm">
          <Link2 className="h-3.5 w-3.5" /> Add a link
        </button>
      </div>

      {!canAttach && (
        <p className="text-erp-xs text-muted-foreground">
          This incident is closed. Attaching is still offered — the server decides, and
          says why if it refuses.
        </p>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files) }}
        className="rounded-md border border-dashed border-border px-3 py-4 text-center text-erp-xs text-muted-foreground"
      >
        Drop files here — photos, video, audio, documents.
      </div>

      {uploads.length > 0 && (
        <ul className="space-y-1">
          {uploads.map((u) => (
            <li key={u.key} className="flex items-center gap-2 text-erp-xs">
              {u.state === 'uploading' && <Loader2 className="h-3 w-3 animate-spin shrink-0" />}
              <span className="truncate flex-1 min-w-0">{u.name}</span>
              <span className={cn(
                'shrink-0',
                u.state === 'failed' ? 'text-red-700 dark:text-red-400' : 'text-muted-foreground',
              )}>
                {u.state === 'uploading' ? 'Uploading…' : u.state === 'done' ? 'Attached' : u.error}
              </span>
              <button type="button" aria-label="Dismiss"
                onClick={() => setUploads((all) => all.filter((x) => x.key !== u.key))}
                className="shrink-0 opacity-60 hover:opacity-100">
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {rows === null ? <LoadingBlock label="Loading evidence…" /> : live.length === 0 ? (
        <EmptyState
          title="No evidence attached yet"
          hint="A report without evidence is still a report — it just stays unverified until somebody corroborates it."
        />
      ) : (
        <ul className="space-y-2">
          {live.map((e) => (
            <EvidenceRow key={e.id} row={e} onWithdraw={() => setWithdrawing(e)} />
          ))}
        </ul>
      )}

      {withdrawn.length > 0 && (
        <details className="text-erp-xs">
          <summary className="cursor-pointer text-muted-foreground">
            {withdrawn.length} withdrawn {withdrawn.length === 1 ? 'item' : 'items'} — kept,
            not deleted
          </summary>
          <ul className="mt-2 space-y-2 opacity-70">
            {withdrawn.map((e) => <EvidenceRow key={e.id} row={e} />)}
          </ul>
        </details>
      )}

      <LinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        onAdd={async (url, caption) => {
          await addEvidence(incidentId, { kindCode: 'LINK', externalUrl: url, caption })
          await reload()
          onChanged?.()
        }}
      />

      <Modal open={liveOpen} onClose={() => setLiveOpen(false)} width="lg"
        title="Live from the scene"
        description="Opens a coverage event for this incident and puts this device on air.">
        <LiveFeedPanel incidentId={incidentId} onAttached={async () => {
          await reload()
          onChanged?.()
        }} />
      </Modal>

      <WithdrawDialog
        row={withdrawing}
        onClose={() => setWithdrawing(null)}
        onWithdraw={async (reason) => {
          if (!withdrawing) return
          await withdrawEvidence(incidentId, withdrawing.id, reason)
          setWithdrawing(null)
          await reload()
          onChanged?.()
        }}
      />
    </div>
  )
}

function EvidenceRow({ row, onWithdraw }: { row: Evidence; onWithdraw?: () => void }) {
  return (
    <li className="erp-card px-3 py-2 flex items-start gap-3">
      <span className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0 pt-0.5 w-28">
        {label(row.kindCode)}
      </span>
      <div className="min-w-0 flex-1">
        <span className="text-erp-sm">
          {row.caption || <span className="text-muted-foreground">No caption</span>}
        </span>
        {row.externalUrl && (
          <div className="text-erp-xs text-muted-foreground break-all">{row.externalUrl}</div>
        )}
        <div className="text-erp-xs text-muted-foreground mt-0.5">
          Added {new Date(row.createdAt).toLocaleString()}
          {row.latitude != null && ` · ${row.latitude}, ${row.longitude}`}
        </div>
        {/* View / listen / watch / read. Evidence you cannot open is a filename, and a
            filename does not corroborate anything. */}
        <EvidenceViewer row={row} />
        {row.withdrawn && (
          <div className="text-erp-xs text-amber-800 dark:text-amber-400 mt-1">
            Withdrawn {row.withdrawnAt && new Date(row.withdrawnAt).toLocaleString()} —{' '}
            {row.withdrawnReason}
          </div>
        )}
      </div>
      {onWithdraw && (
        <button type="button" onClick={onWithdraw} title="Withdraw this item"
          className="shrink-0 text-muted-foreground hover:text-red-700">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  )
}

/**
 * Record audio or video in the browser.
 *
 * The tracks are released on stop AND on unmount. A camera light left on after the panel
 * closes is a bug users notice and do not forgive — and rightly, since the page has no
 * business holding the camera once nobody is looking at it.
 */
function Recorder({
  kind, onRecorded,
}: {
  kind: 'AUDIO' | 'VIDEO'
  onRecorded: (blob: Blob, filename: string, kindCode: string) => Promise<void>
}) {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const cap = kind === 'AUDIO' ? MAX_AUDIO_SECONDS : MAX_VIDEO_SECONDS

  const release = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop())
    stream.current = null
    recorder.current = null
  }, [])

  useEffect(() => release, [release])

  useEffect(() => {
    if (!recording) return
    const t = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [recording])

  useEffect(() => {
    if (recording && seconds >= cap) recorder.current?.stop()
  }, [recording, seconds, cap])

  const start = async () => {
    setError(null)
    try {
      const media = await navigator.mediaDevices.getUserMedia(
        kind === 'AUDIO' ? { audio: true } : { audio: true, video: true })
      stream.current = media
      const chunks: BlobPart[] = []
      const rec = new MediaRecorder(media)
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType })
        release()
        setRecording(false)
        setSeconds(0)
        const ext = rec.mimeType.includes('webm') ? 'webm' : rec.mimeType.includes('mp4') ? 'mp4' : 'bin'
        void onRecorded(blob, `incident-${kind.toLowerCase()}-${Date.now()}.${ext}`, kind)
      }
      recorder.current = rec
      rec.start()
      setSeconds(0)
      setRecording(true)
    } catch (err) {
      // A refused microphone is not a broken app. Say so and leave every other path open.
      setError(err instanceof Error ? err.message : 'This device would not start recording.')
      release()
    }
  }

  return (
    <div className="inline-flex flex-col">
      <button type="button"
        onClick={() => (recording ? recorder.current?.stop() : void start())}
        className={cn(
          'erp-btn h-9 px-3 inline-flex items-center gap-2 text-erp-sm',
          recording && 'border-red-400 text-red-700',
        )}>
        {recording ? <Square className="h-3.5 w-3.5" />
          : kind === 'AUDIO' ? <Mic className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
        {recording
          ? `Stop · ${seconds}s / ${cap}s`
          : kind === 'AUDIO' ? 'Record audio' : 'Record video'}
      </button>
      {error && <span className="text-erp-xs text-red-700 dark:text-red-400 mt-1 max-w-56">{error}</span>}
    </div>
  )
}

function LinkDialog({
  open, onClose, onAdd,
}: {
  open: boolean
  onClose: () => void
  onAdd: (url: string, caption: string | null) => Promise<void>
}) {
  const [url, setUrl] = useState('')
  const [caption, setCaption] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal open={open} onClose={onClose} title="Attach a link"
      description="A tweet, a news article, a court filing — anything the desk can follow."
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>Cancel</button>
          <button type="button" disabled={busy || !url.trim()}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2"
            onClick={async () => {
              setBusy(true); setError(null)
              try {
                await onAdd(url.trim(), caption.trim() || null)
                setUrl(''); setCaption(''); onClose()
              } catch (err) { setError(explain(err)) } finally { setBusy(false) }
            }}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Attach
          </button>
        </>
      )}>
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Link</span>
          <input className="erp-input h-9 w-full" type="url" value={url} placeholder="https://…"
            onChange={(e) => setUrl(e.target.value)} />
        </label>
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">What it shows</span>
          <input className="erp-input h-9 w-full" value={caption}
            onChange={(e) => setCaption(e.target.value)} />
        </label>
      </div>
    </Modal>
  )
}

function WithdrawDialog({
  row, onClose, onWithdraw,
}: {
  row: Evidence | null
  onClose: () => void
  onWithdraw: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal open={row !== null} onClose={onClose} title="Withdraw this evidence"
      description="The row is kept and stays in the record. It leaves the live list, and the timeline says who withdrew it and why."
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>Cancel</button>
          <button type="button" disabled={busy || !reason.trim()}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2"
            onClick={async () => {
              setBusy(true); setError(null)
              try { await onWithdraw(reason.trim()); setReason('') }
              catch (err) { setError(explain(err)) } finally { setBusy(false) }
            }}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Withdraw
          </button>
        </>
      )}>
      <div className="space-y-3">
        <ErrorStrip message={error} className="rounded-md border" />
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">
            Why — required. This is the record of the retraction.
          </span>
          <textarea rows={3} className="erp-input w-full py-2" value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
    </Modal>
  )
}

/** A courtesy guess at the kind from the browser's content type. The server decides. */
function kindOf(contentType: string): string {
  if (contentType.startsWith('image/')) return 'PHOTO'
  if (contentType.startsWith('video/')) return 'VIDEO'
  if (contentType.startsWith('audio/')) return 'AUDIO'
  return 'DOCUMENT'
}
